<script lang="ts">
  import { onMount } from 'svelte';
  import { slide } from 'svelte/transition';
  import {
    deleteCountdown,
    getCountdown,
    saveCountdown,
    type CompactUnit,
    type Countdown,
    type Kind,
  } from '../lib/storage';
  import {
    DEFAULT_FORMAT_UNITS_BITMASK,
    parseFormatUnits,
    UNIT_ORDER,
    unitsToBitmask,
    type Unit,
  } from '../lib/countdown';
  import { dateToLocalISO, localISOToDate } from '../lib/format';
  import { hexStringToInt, intToHexString, colorForDate, DEFAULT_PRESETS } from '../lib/color';
  import { firstGrapheme } from '../lib/emoji';
  import { uuid } from '../lib/id';
  import { navigate } from '../lib/router';
  import CountdownCard from '../components/CountdownCard.svelte';

  type Props = { id?: string };
  let { id }: Props = $props();

  const isEdit = $derived(!!id);

  function defaultDateISO(): string {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return dateToLocalISO(d);
  }

  let title = $state('');
  // Empty so the placeholder ("🗓️", matching the iOS storyboard's emoji
  // textfield placeholder) shows. firstGrapheme falls back to 🎉 on save if
  // the user leaves it empty, so we never persist a blank emoji.
  let emoji = $state('');
  let dateLocal = $state(defaultDateISO());
  // All-day by default, like the default date above (tomorrow at midnight).
  let allDay = $state(true);
  let colorHex = $state(intToHexString(colorForDate(localISOToDate(defaultDateISO()))));
  // Mirrors iOS's `hasUserSelectedColor` (CreateOrEditCountdownViewController):
  // while it's false on a NEW countdown, changing the date auto-recomputes the
  // color via colorForDate. Once the user picks a swatch or a custom value,
  // the auto-update stops so we don't overwrite their choice on every retoque.
  let hasUserSelectedColor = $state(false);
  // Reveal trigger for the preview card — we don't want it to look like one
  // more editable section when the form first loads. It slides in only after
  // the user actually tweaks the date or color (the two fields that drive
  // the card's visual identity), matching iOS's
  // `startShowingCountdownPreviewIfNeeded` reveal behaviour.
  let hasEditedDate = $state(false);
  let kind: Kind = $state(null);
  let originYear = $state<number | null>(null);
  let selectedUnits = $state<Unit[]>(parseFormatUnits(DEFAULT_FORMAT_UNITS_BITMASK));
  let compactUnit: CompactUnit = $state('Day');
  let countOnlyWeekdays = $state(false);
  let saving = $state(false);

  onMount(async () => {
    if (!id) return;
    const c = await getCountdown(id);
    if (!c) return;
    title = c.title;
    emoji = c.emoji;
    dateLocal = dateToLocalISO(new Date(c.dueDate));
    allDay = c.allDay;
    colorHex = intToHexString(c.colorHex);
    // Editing an existing countdown — the stored color is the user's choice,
    // so any date tweak in this session should leave it alone.
    hasUserSelectedColor = true;
    kind = c.kind;
    originYear = c.originYear;
    selectedUnits = parseFormatUnits(c.formatUnits);
    compactUnit = c.compactUnit ?? 'Day';
    countOnlyWeekdays = c.countOnlyWeekdays ?? false;
  });

  /// Recompute the auto color when the user changes the date on a new
  /// countdown — same trigger as iOS's `dueDatePickerValueChanged`. Once the
  /// user touches the color picker (preset or custom), `hasUserSelectedColor`
  /// flips and this no-ops.
  $effect(() => {
    const date = dateLocal;
    if (isEdit || hasUserSelectedColor) return;
    try {
      colorHex = intToHexString(colorForDate(localISOToDate(date)));
    } catch {
      // malformed date — keep current color
    }
  });

  function selectColor(hex: string) {
    colorHex = hex;
    hasUserSelectedColor = true;
  }

  const isCustomColor = $derived(
    !DEFAULT_PRESETS.some((p) => intToHexString(p).toLowerCase() === colorHex.toLowerCase()),
  );

  /// Preview reveals on first user-driven date or color change. Edit mode
  /// shows it from the start (the stored countdown is already meaningful).
  const previewVisible = $derived(isEdit || hasEditedDate || hasUserSelectedColor);

  let dateInputEl: HTMLInputElement | undefined = $state(undefined);

  /// Open the browser's native date picker without showing the inline
  /// numeric editor that Chrome desktop renders inside the input by default
  /// (the ugly mm/dd/yyyy spinners). showPicker() exists in Chromium 99+,
  /// Safari 16+, Firefox 101+ — on older browsers we fall back to focusing
  /// the input so the user can still pick a date, just with the legacy UI.
  function openDatePicker() {
    if (!dateInputEl) return;
    if (typeof dateInputEl.showPicker === 'function') {
      try {
        dateInputEl.showPicker();
        return;
      } catch {
        // Some browsers throw if not user-initiated; fall through.
      }
    }
    dateInputEl.focus();
    dateInputEl.click();
  }

  /// All-day on: the time is dropped (the countdown lives at local midnight
  /// of its day, as on iOS) and the picker becomes a date-only one.
  function setAllDay(on: boolean) {
    allDay = on;
    if (on) dateLocal = dateLocal.slice(0, 10) + 'T00:00';
    hasEditedDate = true;
  }

  const dateDisplay = $derived.by(() => {
    try {
      const d = localISOToDate(dateLocal);
      const opts: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      };
      if (!allDay) {
        opts.hour = 'numeric';
        opts.minute = '2-digit';
      }
      return d.toLocaleString(navigator.language, opts);
    } catch {
      return '—';
    }
  });

  /// Live draft used by the preview CountdownCard above the form. Mirrors
  /// what gets persisted on save (same field shape as Countdown) so the
  /// preview is always WYSIWYG — including past-state styling and the
  /// auto-color cycle when the date moves.
  const draft = $derived.by<Countdown>(() => {
    let dueDateISO: string;
    try {
      dueDateISO = localISOToDate(dateLocal).toISOString();
    } catch {
      dueDateISO = new Date().toISOString();
    }
    const units =
      selectedUnits.length > 0 ? selectedUnits : parseFormatUnits(DEFAULT_FORMAT_UNITS_BITMASK);
    return {
      id: id ?? 'preview',
      title: title.trim() || 'New countdown',
      emoji: emoji || '🗓️',
      dueDate: dueDateISO,
      allDay,
      colorHex: hexStringToInt(colorHex),
      kind,
      originYear: kind ? originYear : null,
      formatUnits: unitsToBitmask(units),
      compactUnit,
      countOnlyWeekdays,
      createdAt: 0,
      updatedAt: 0,
    };
  });

  function toggleUnit(unit: Unit) {
    if (selectedUnits.includes(unit)) {
      selectedUnits = selectedUnits.filter((u) => u !== unit);
    } else {
      selectedUnits = [...selectedUnits, unit].sort(
        (a, b) => UNIT_ORDER.indexOf(a) - UNIT_ORDER.indexOf(b),
      );
    }
  }

  const canSave = $derived(title.trim().length > 0 && dateLocal.length > 0 && !saving);

  async function save() {
    if (!canSave) return;
    saving = true;
    try {
      const cleanEmoji = firstGrapheme(emoji);
      const now = Date.now();
      const units =
        selectedUnits.length > 0 ? selectedUnits : parseFormatUnits(DEFAULT_FORMAT_UNITS_BITMASK);
      const c: Countdown = {
        id: id ?? uuid(),
        title: title.trim(),
        emoji: cleanEmoji,
        dueDate: localISOToDate(dateLocal).toISOString(),
        allDay,
        colorHex: hexStringToInt(colorHex),
        kind,
        originYear: kind ? originYear : null,
        formatUnits: unitsToBitmask(units),
        compactUnit,
        countOnlyWeekdays,
        createdAt: now,
        updatedAt: now,
      };
      await saveCountdown(c);
      navigate(`/c/${c.id}`, true);
    } finally {
      saving = false;
    }
  }

  async function del() {
    if (!id) return;
    if (!confirm('Delete this countdown?')) return;
    await deleteCountdown(id);
    navigate('/', true);
  }

  const unitLabelText: Record<Unit, string> = {
    Year: 'Years',
    Month: 'Months',
    Week: 'Weeks',
    Day: 'Days',
    Hour: 'Hours',
    Minute: 'Minutes',
    Second: 'Seconds',
  };

  // Segments + per-kind row copy mirror the iOS storyboard and
  // CreateOrEditCountdownViewController.configureForOccasionType.
  const KIND_SEGMENTS: { value: 'none' | 'birthday' | 'anniversary'; label: string }[] = [
    { value: 'none', label: 'None' },
    { value: 'birthday', label: 'Birthday 🎂' },
    { value: 'anniversary', label: 'Anniversary ❤️' },
  ];

  // Order + copy match iOS's CompactDisplayUnitSegmentedControlIndexes
  // (Days/Weeks/Months/Years) and displayDescription's "Will show time
  // remaining in …" caption shown below the control.
  const COMPACT_SEGMENTS: { value: CompactUnit; label: string; caption: string }[] = [
    { value: 'Day',   label: 'Days',   caption: 'Will show time remaining in days'   },
    { value: 'Week',  label: 'Weeks',  caption: 'Will show time remaining in weeks'  },
    { value: 'Month', label: 'Months', caption: 'Will show time remaining in months' },
    { value: 'Year',  label: 'Years',  caption: 'Will show time remaining in years'  },
  ];
  const compactCaption = $derived(
    COMPACT_SEGMENTS.find((s) => s.value === compactUnit)?.caption ?? '',
  );

  function setKind(v: 'none' | 'birthday' | 'anniversary') {
    kind = v === 'none' ? null : v;
    if (!kind) originYear = null;
  }

  /// Emoji picker is lazy-loaded on first open so the ~80KB chunk doesn't
  /// affect cold start for users who never edit a countdown. We open the
  /// overlay immediately and swap a loading line for the picker element
  /// once the dynamic import resolves — the first tap feels responsive
  /// even on slow networks.
  let pickerOpen = $state(false);
  let pickerLoaded = $state(false);
  let pickerEl: HTMLElement | undefined = $state(undefined);

  async function openPicker() {
    pickerOpen = true;
    if (!pickerLoaded) {
      await import('emoji-picker-element');
      pickerLoaded = true;
    }
  }

  function closePicker() {
    pickerOpen = false;
  }

  $effect(() => {
    if (!pickerEl) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ unicode?: string }>).detail;
      if (detail?.unicode) {
        emoji = detail.unicode;
        closePicker();
      }
    };
    pickerEl.addEventListener('emoji-click', handler);
    return () => pickerEl?.removeEventListener('emoji-click', handler);
  });
</script>

<main>
  <header>
    <button class="cancel" onclick={() => navigate('/')}>Cancel</button>
    <h1>{isEdit ? 'Edit' : 'New countdown'}</h1>
    <button class="save" onclick={save} disabled={!canSave}>{isEdit ? 'Save' : 'Add'}</button>
  </header>

  <form onsubmit={(e) => { e.preventDefault(); save(); }}>
    {#if previewVisible}
      <section class="preview-section" transition:slide={{ duration: 220 }}>
        <h2>Preview</h2>
        <div class="preview-card-wrap">
          <CountdownCard countdown={draft} interactive={false} />
        </div>
      </section>
    {/if}

    <section>
      <h2>Pick a title and emoji</h2>
      <div class="card">
        <div class="row title-row">
          <button
            type="button"
            class="emoji-btn"
            class:placeholder={!emoji}
            onclick={openPicker}
            aria-label="Pick emoji"
          >
            {emoji || '🗓️'}
          </button>
          <input
            class="title-input"
            type="text"
            bind:value={title}
            placeholder="Name your countdown"
            autocomplete="off"
            maxlength="120"
            aria-label="Title"
          />
        </div>
      </div>
    </section>

    <section>
      <h2>Pick a date</h2>
      <div class="card">
        <label class="row toggle-row">
          <span class="row-label">All-day</span>
          <input
            type="checkbox"
            checked={allDay}
            onchange={(e) => setAllDay(e.currentTarget.checked)}
          />
        </label>
        <div class="row date-row">
          <span class="row-label">{allDay ? 'Date' : 'Date & time'}</span>
          <button type="button" class="date-trigger" onclick={openDatePicker}>
            {dateDisplay}
          </button>
          {#if allDay}
            <input
              bind:this={dateInputEl}
              class="hidden-date"
              type="date"
              value={dateLocal.slice(0, 10)}
              oninput={(e) => { dateLocal = e.currentTarget.value + 'T00:00'; hasEditedDate = true; }}
              aria-hidden="true"
              tabindex="-1"
            />
          {:else}
            <input
              bind:this={dateInputEl}
              class="hidden-date"
              type="datetime-local"
              bind:value={dateLocal}
              oninput={() => { hasEditedDate = true; }}
              aria-hidden="true"
              tabindex="-1"
            />
          {/if}
        </div>
      </div>
    </section>

    <section>
      <h2>Pick a color</h2>
      <div class="card">
        <div class="row swatches-row">
          {#each DEFAULT_PRESETS as preset (preset)}
            {@const hex = intToHexString(preset)}
            <button
              type="button"
              class="swatch"
              class:selected={!isCustomColor && colorHex.toLowerCase() === hex.toLowerCase()}
              style="background: {hex}"
              onclick={() => selectColor(hex)}
              aria-label="Use preset color"
            ></button>
          {/each}
          <label
            class="swatch custom"
            class:selected={isCustomColor}
            style:background={isCustomColor ? colorHex : undefined}
            aria-label="Custom color"
          >
            <input
              class="custom-input"
              type="color"
              bind:value={colorHex}
              onchange={() => { hasUserSelectedColor = true; }}
            />
          </label>
        </div>
      </div>
    </section>

    <section>
      <h2>Special Occasions</h2>
      <div class="card">
        <div class="row segmented-row">
          <div class="segmented" role="radiogroup" aria-label="Occasion type">
            {#each KIND_SEGMENTS as seg (seg.value)}
              <input
                type="radio"
                name="kind"
                id="kind-{seg.value}"
                value={seg.value}
                checked={(kind ?? 'none') === seg.value}
                onchange={() => setKind(seg.value)}
              />
              <label for="kind-{seg.value}">{seg.label}</label>
            {/each}
          </div>
        </div>
        {#if kind}
          <label class="row" for="origin">
            <span class="row-label">
              {kind === 'birthday' ? 'Birth year or current age' : 'Year'}
            </span>
            <input
              id="origin"
              class="value-input year-input"
              type="number"
              min="0"
              max="2200"
              bind:value={originYear}
              placeholder={kind === 'birthday' ? 'e.g. 1990 or 34' : 'e.g. 2020'}
            />
          </label>
        {/if}
      </div>
    </section>

    <section>
      <h2>Pick compact format <span class="h2-hint">(shown in the list)</span></h2>
      <div class="card">
        <div class="row segmented-row">
          <div class="segmented" role="radiogroup" aria-label="Compact format">
            {#each COMPACT_SEGMENTS as seg (seg.value)}
              <input
                type="radio"
                name="compact"
                id="compact-{seg.value}"
                value={seg.value}
                checked={compactUnit === seg.value}
                onchange={() => (compactUnit = seg.value)}
              />
              <label for="compact-{seg.value}">{seg.label}</label>
            {/each}
          </div>
        </div>
        <div class="caption">{compactCaption}</div>
      </div>
    </section>

    <section>
      <h2>Pick detailed format <span class="h2-hint">(shown when opened)</span></h2>
      <div class="card">
        {#each UNIT_ORDER as unit (unit)}
          <label class="row toggle-row">
            <span class="row-label">{unitLabelText[unit]}</span>
            <input
              type="checkbox"
              checked={selectedUnits.includes(unit)}
              onchange={() => toggleUnit(unit)}
            />
          </label>
        {/each}
        <label class="row toggle-row">
          <span class="row-label">Count only weekdays</span>
          <input type="checkbox" bind:checked={countOnlyWeekdays} />
        </label>
      </div>
    </section>

    {#if isEdit}
      <section>
        <button type="button" class="delete card" onclick={del}>Delete countdown</button>
      </section>
    {/if}
  </form>

  {#if pickerOpen}
    <div class="emoji-overlay" role="dialog" aria-label="Pick an emoji">
      <button
        type="button"
        class="emoji-backdrop"
        onclick={closePicker}
        aria-label="Close emoji picker"
      ></button>
      {#if pickerLoaded}
        <emoji-picker class="emoji-sheet" bind:this={pickerEl}></emoji-picker>
      {:else}
        <div class="emoji-loading">Loading…</div>
      {/if}
    </div>
  {/if}
</main>

<style>
  main {
    background: var(--ios-bg);
    min-height: 100dvh;
    padding-bottom: max(40px, env(safe-area-inset-bottom));
    /* Match the iPad form sheet width used in
       CreateOrEditCountdownViewController.instantiate (540×860) so the form
       doesn't sprawl across wide desktop windows. */
    max-width: 560px;
    margin: 0 auto;
  }

  header {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 8px;
    padding: max(12px, env(safe-area-inset-top)) 8px 12px;
    position: sticky;
    top: 0;
    background: var(--ios-bg);
    z-index: 10;
  }
  h1 {
    font-size: 1rem;
    font-weight: 600;
    margin: 0;
    text-align: center;
    color: var(--ios-label);
  }
  /* Pill overlay matching the Detail view's nav buttons. We use the iOS
     segmented-control gray (rgba(120,120,128,0.16) light, 0.24 dark) so the
     pill reads on the form's --ios-bg without going invisible in either
     mode. iOS tint for the label keeps Cancel + Add tappable-looking. */
  .cancel,
  .save {
    appearance: none;
    border: 0;
    background: var(--ios-segment-bg);
    color: var(--ios-tint);
    font: inherit;
    font-size: 0.95rem;
    height: 34px;
    padding: 0 14px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
  }
  .save {
    font-weight: 600;
  }
  .save:disabled {
    color: var(--ios-label-tertiary);
    cursor: not-allowed;
  }

  form {
    display: flex;
    flex-direction: column;
  }

  /* Live preview of how the countdown will render in the home list. Sits
     as the first section of the form (only after the user has touched the
     date or color — see previewVisible) so it doesn't feel editable when
     blank. The PREVIEW h2 reuses the same uppercase secondary style as the
     real form section headers so the visual hierarchy says "this is a
     labelled output, not an input". */
  .preview-section {
    margin-top: 16px;
  }
  .preview-card-wrap {
    margin: 0 16px;
  }

  section {
    margin-top: 24px;
  }
  section h2 {
    font-size: 0.78rem;
    font-weight: 400;
    color: var(--ios-label-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 6px;
    padding: 0 24px;
  }
  /* Parenthetical helper inside the section header — same color/size but
     without the uppercase + letter-spacing so it reads as a quiet aside,
     not as part of the all-caps label. */
  .h2-hint {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
    opacity: 0.85;
  }

  .card {
    background: var(--ios-card-bg);
    border-radius: 10px;
    margin: 0 16px;
    overflow: hidden;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 16px;
    min-height: 44px;
    border-top: 0.5px solid var(--ios-separator);
    color: var(--ios-label);
  }
  .row:first-child {
    border-top: 0;
  }
  .row-label {
    flex: 1;
    font-size: 1rem;
  }

  .value-input {
    appearance: none;
    -webkit-appearance: none;
    border: 0;
    background: transparent;
    color: var(--ios-tint);
    font: inherit;
    font-size: 1rem;
    text-align: right;
    padding: 0;
    cursor: pointer;
  }
  .value-input::placeholder {
    color: var(--ios-label-tertiary);
  }
  .year-input {
    width: 14ch;
  }

  /* Date trigger: a button styled like an iOS interactive value (right-aligned,
     tint color) that opens the browser's native date picker via showPicker().
     The real <input type="datetime-local"> is parked off-screen so we never
     see Chrome desktop's inline mm/dd/yyyy spinners. */
  .date-row {
    cursor: pointer;
    position: relative;
  }
  .date-trigger {
    appearance: none;
    border: 0;
    background: transparent;
    color: var(--ios-tint);
    font: inherit;
    font-size: 1rem;
    cursor: pointer;
    padding: 0;
    text-align: right;
  }
  .hidden-date {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: 0;
    border: 0;
    opacity: 0;
    pointer-events: none;
    /* Anchor at the trigger so the native picker dropdown lines up with it
       in browsers that position the dropdown next to the input. */
    right: 16px;
    bottom: 0;
  }

  /* Title + emoji combined row, mirroring iOS "Pick a title and emoji". */
  .title-row {
    gap: 12px;
  }
  .emoji-btn {
    appearance: none;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 1.4rem;
    width: 2.5rem;
    text-align: center;
    color: var(--ios-label);
    padding: 0;
    cursor: pointer;
    line-height: 1;
  }
  .emoji-btn.placeholder {
    color: var(--ios-label-tertiary);
  }
  .title-input {
    appearance: none;
    -webkit-appearance: none;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 1rem;
    color: var(--ios-label);
    flex: 1;
    min-width: 0;
    padding: 0;
  }
  .title-input::placeholder {
    color: var(--ios-label-tertiary);
  }

  /* Preset swatches + a "custom" swatch wrapping a hidden native color input.
     Mirrors the iOS app's color picker section: 6 preset circles + 1 cell that
     opens the system color picker. Selected swatch shows the iOS-tint ring
     (same convention iOS uses to indicate the active preset). */
  .swatches-row {
    flex-wrap: wrap;
    gap: 14px;
    padding: 16px;
    justify-content: center;
  }
  .swatch {
    appearance: none;
    border: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    cursor: pointer;
    padding: 0;
    margin: 0;
    position: relative;
    transition: transform 0.1s ease;
  }
  .swatch:active { transform: scale(0.94); }
  .swatch.selected {
    outline: 2.5px solid var(--ios-tint);
    outline-offset: 3px;
  }
  .swatch.custom {
    background: conic-gradient(
      from 0deg,
      #ff3b30,
      #ff9500,
      #ffcc00,
      #34c759,
      #00c7be,
      #007aff,
      #5856d6,
      #af52de,
      #ff2d55,
      #ff3b30
    );
    overflow: hidden;
    display: inline-block;
  }
  .custom-input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  /* iOS UISegmentedControl: rounded pill with a "thumb" on the selected
     segment. Storyboard:241-243 segments are "None" / "Birthday 🎂" /
     "Anniversary ❤️". */
  .segmented-row {
    padding: 8px;
  }
  .segmented {
    display: flex;
    flex: 1;
    background: var(--ios-segment-bg);
    border-radius: 9px;
    padding: 2px;
    position: relative;
  }
  .segmented input[type='radio'] {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }
  .segmented label {
    flex: 1;
    text-align: center;
    padding: 7px 4px;
    border-radius: 7px;
    font-size: 0.85rem;
    cursor: pointer;
    color: var(--ios-label);
    line-height: 1.2;
    transition: background 0.15s ease;
    min-width: 0;
  }
  .segmented input[type='radio']:checked + label {
    background: var(--ios-segment-selected);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.06), 0 1px 0 rgba(0, 0, 0, 0.04);
    font-weight: 600;
  }
  .segmented input[type='radio']:focus-visible + label {
    outline: 2px solid var(--ios-tint);
    outline-offset: 2px;
  }

  /* "Will show time remaining in …" footnote under the compact-format
     segmented control. Same role as the iOS storyboard's secondary label
     directly below the control — explains what the choice does. */
  .caption {
    padding: 4px 14px 12px;
    font-size: 0.78rem;
    color: var(--ios-label-secondary);
    line-height: 1.3;
  }

  .toggle-row {
    cursor: pointer;
  }
  .toggle-row input[type='checkbox'] {
    appearance: auto;
    width: 1.25rem;
    height: 1.25rem;
    accent-color: var(--ios-tint);
    cursor: pointer;
  }

  .delete {
    appearance: none;
    display: block;
    /* No explicit width — the .card class already gives us
       `margin: 0 16px`, and a block-level element auto-sizes to
       (parent − margins). `width: 100%` made it 100% of the section
       AND added the 16px margin on top, overflowing by 32px. */
    padding: 14px;
    background: var(--ios-card-bg);
    color: var(--ios-destructive);
    border: 0;
    border-radius: 10px;
    font: inherit;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
  }
  .delete:active {
    background: color-mix(in srgb, var(--ios-destructive) 10%, var(--ios-card-bg));
  }

  /* Emoji picker as a bottom sheet — translucent backdrop, picker anchored
     to the bottom. emoji-picker-element brings its own background so we just
     position it. The backdrop is a real <button> for accessibility (clicking
     outside the sheet dismisses, and screen readers see it as "Close"). */
  .emoji-overlay {
    position: fixed;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }
  .emoji-backdrop {
    appearance: none;
    position: absolute;
    inset: 0;
    border: 0;
    padding: 0;
    margin: 0;
    background: rgba(0, 0, 0, 0.4);
    cursor: pointer;
  }
  .emoji-sheet {
    position: relative;
    z-index: 1;
    margin-bottom: env(safe-area-inset-bottom);
    max-width: 100vw;
  }
  .emoji-loading {
    position: relative;
    z-index: 1;
    margin-bottom: max(20px, env(safe-area-inset-bottom));
    padding: 20px 32px;
    background: var(--ios-card-bg);
    color: var(--ios-label-secondary);
    border-radius: 14px;
    font-size: 0.9rem;
  }
</style>
